import React, { useRef, useState, useLayoutEffect, useCallback } from 'react';
import { cn } from '@/shared/utils';
import { Tooltip } from '../organisms/Tooltip';
import type { TextColor } from '@/shared/types/text';
import { SYSTEM_COLORS } from '@/modules/Text/components/constants';

/**
 * Con `text-overflow: ellipsis`, en Chromium/WebKit suele cumplirse `scrollWidth === clientWidth`
 * aunque el texto esté recortado; el tooltip quedaría deshabilitado si solo usáramos esa comparación.
 */
function isTextTruncatedInElement(el: HTMLElement): boolean {
  if (el.scrollWidth > el.clientWidth) {
    return true;
  }
  const text = el.textContent ?? '';
  if (!text.trim()) {
    return false;
  }
  const containerWidth = el.getBoundingClientRect().width;
  if (containerWidth < 1) {
    return false;
  }
  const style = window.getComputedStyle(el);
  const clone = document.createElement('span');
  clone.setAttribute('aria-hidden', 'true');
  clone.style.position = 'absolute';
  clone.style.visibility = 'hidden';
  clone.style.whiteSpace = 'nowrap';
  clone.style.top = '0';
  clone.style.left = '0';
  clone.style.pointerEvents = 'none';
  clone.style.font = style.font;
  clone.style.fontSize = style.fontSize;
  clone.style.fontFamily = style.fontFamily;
  clone.style.fontWeight = style.fontWeight;
  clone.style.fontStyle = style.fontStyle;
  clone.style.letterSpacing = style.letterSpacing;
  clone.style.textTransform = style.textTransform;
  clone.textContent = text;
  document.body.appendChild(clone);
  try {
    const naturalWidth = clone.getBoundingClientRect().width;
    return naturalWidth > containerWidth + 0.5;
  } finally {
    document.body.removeChild(clone);
  }
}

export interface TruncatedWithTooltipProps {
  /** Contenido a mostrar (se trunca con ellipsis si no cabe). */
  children: React.ReactNode;
  /** Texto completo para el tooltip. Si no se pasa, se usa `children` solo cuando es string. */
  title?: React.ReactNode;
  /** Clases CSS del span que contiene el texto truncado. */
  className?: string;
  /**
   * Límite estricto de **caracteres** visibles (p. ej. ratings): se muestran como máximo `N`
   * caracteres y, si el texto es más largo, se añade `…` y el tooltip lleva el valor completo.
   * Requiere `title` o `children` en forma de `string`. Si es &gt; 0, **anula** el truncado por CSS
   * de esta instancia.
   */
  maxVisibleChars?: number;
  /**
   * Color del texto visible (tokens `ui-text-*` o color CSS arbitrario). Útil cuando el contenedor
   * no hereda el color del contenido (p. ej. celdas de tabla con truncado).
   */
  textColor?: TextColor | (string & {});
  /** Posición del tooltip (por defecto `top`). */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Si es false, no se aplica truncado ni tooltip (solo se renderiza children con className). */
  truncate?: boolean;
  /** Si es false, no se muestra tooltip al truncar (solo truncado). Por defecto true. */
  showTooltipWhenTruncated?: boolean;
}

function sourceStringForCharCap(title: React.ReactNode, children: React.ReactNode): string {
  if (typeof title === 'string') {
    return title;
  }
  if (typeof children === 'string') {
    return children;
  }
  return '';
}

/**
 * Envuelve texto en truncado CSS (ellipsis) o, con {@link TruncatedWithTooltipProps.maxVisibleChars},
 * en un máximo de caracteres + `…`. El tooltip muestra el texto completo cuando aplica.
 */
export const TruncatedWithTooltip = ({
  children,
  title,
  className = '',
  textColor,
  position = 'top',
  truncate = true,
  showTooltipWhenTruncated = true,
  maxVisibleChars,
}: TruncatedWithTooltipProps) => {
  const useCharCap = typeof maxVisibleChars === 'number' && maxVisibleChars > 0;

  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const isSystemTextColor = textColor && SYSTEM_COLORS.includes(textColor as TextColor);
  const textColorClass = isSystemTextColor ? `ui-text-${textColor}` : '';
  const textColorStyle =
    textColor && !isSystemTextColor ? { color: textColor as string } : undefined;

  const checkTruncation = useCallback(() => {
    if (useCharCap) {
      setIsTruncated(false);
      return;
    }
    const el = ref.current;
    if (!el || !truncate) {
      setIsTruncated(false);
      return;
    }
    setIsTruncated(isTextTruncatedInElement(el));
  }, [truncate, useCharCap]);

  useLayoutEffect(() => {
    if (useCharCap) return;
    checkTruncation();
    const el = ref.current;
    if (!el || !truncate) return;
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [children, truncate, checkTruncation, useCharCap]);

  if (useCharCap) {
    const fullText = sourceStringForCharCap(title, children).trim();
    const max = maxVisibleChars as number;
    const visible = fullText.length > max ? `${fullText.slice(0, max)}…` : fullText;
    const needsTooltip = showTooltipWhenTruncated && fullText.length > max;

    const span = (
      <span
        style={textColorStyle}
        className={cn('ui-inline-block ui-max-w-full', textColorClass, className)}
      >
        {visible}
      </span>
    );

    if (!needsTooltip) {
      return span;
    }

    return (
      <Tooltip label={fullText} disabled={false} position={position} preserveLayout>
        <div className="ui-block ui-w-full ui-min-w-0 ui-max-w-full">{span}</div>
      </Tooltip>
    );
  }

  const tooltipLabel = showTooltipWhenTruncated
    ? (title ?? (typeof children === 'string' ? children : ''))
    : '';

  const span = (
    <span
      ref={ref}
      style={textColorStyle}
      className={cn(
        truncate && 'ui-block ui-min-w-0 ui-max-w-full ui-truncate',
        textColorClass,
        className
      )}
    >
      {children}
    </span>
  );

  if (!truncate || !tooltipLabel) {
    return span;
  }

  // Envolvemos en un div para que el Tooltip use el div como trigger (ref + hover)
  // y nuestro ref siga en el span para medir truncado; si pasáramos el span, el Tooltip
  // lo clonaría con su ref y perderíamos la medición.
  return (
    <Tooltip label={tooltipLabel} disabled={!isTruncated} position={position} preserveLayout>
      <div className="ui-block ui-w-full ui-min-w-0 ui-max-w-full ui-overflow-hidden">{span}</div>
    </Tooltip>
  );
};
